<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Help Desk"
      subtitle="Manage and track resident support tickets"
      :show-view="true"
      :show-delete="true"
      @view="$router.push('/societyadmin/complaints/view/' + $event.id)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="complaints"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <!-- Header Extra: Tabs & Filter -->
      <template #header-extra>
        <div class="header-controls">
          <div class="tab-pill-wrap">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="tab-pill-btn"
              :class="{ active: currentTab === tab.id }"
              @click="switchTab(tab.id)"
            >
              <v-icon size="16" class="mr-2" :color="currentTab === tab.id ? 'var(--btn-color)' : 'var(--btn-color-dark)'">{{ tab.icon }}</v-icon>
              {{ tab.name }}
            </button>
          </div>

          <div class="status-filter-wrap">
            <FilterDropdown
              v-model="current_status"
              :options="filter_status"
              item-text="name"
              item-value="value"
              placeholder="Filter by Status"
              all-label="All"
              @change="onFilterChange"
            />
          </div>
        </div>
      </template>

      <!-- Custom Cells -->
      <template #col-added_by="{ item }">
        <div class="added-by-cell">
          <v-icon size="14" class="mr-1" color="#94A3B8">mdi-account-outline</v-icon>
          <span>{{ item.added_by }}</span>
        </div>
      </template>

      <template #col-house_number="{ item }">
        <div class="house-number-badge" v-if="item.house_number">
            <v-icon
            size="14"
            color="#94A3B8"
            v-if="item.unit_type === '1'"
          >
            mdi-home-outline
          </v-icon>

          <v-icon
            size="14"
            color="#94A3B8"
            v-else-if="item.unit_type === '2'"
          >
            mdi-store-outline
          </v-icon>
          <span v-if="item.flat_name" class="flat-prefix">{{ item.flat_name }}-</span>
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <template #col-title="{ item }">
        <v-tooltip
          bottom
          nudge-bottom="4"
          color="primary"
          max-width="420"
          :disabled="
            !(item.title && item.title.length > 30) &&
            !(item.description && item.description.length > 40)
          "
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="title-cell" v-bind="attrs" v-on="on">
              <span class="ticket-title">{{ item.title }}</span>
              <span class="ticket-desc" v-if="item.description">{{
                item.description
              }}</span>
            </div>
          </template>
          <div>
            <div style="font-weight: 700; margin-bottom: 2px">
              {{ item.title }}
            </div>
            <div
              v-if="item.description"
              style="font-size: 12px; opacity: 0.85; white-space: pre-line"
            >
              {{ item.description }}
            </div>
          </div>
        </v-tooltip>
      </template>

      <template #col-priority="{ item }">
        <div class="priority-pill" :style="getPriorityStyles(item.priority)">
          <v-icon size="10" :color="getPriorityColor(item.priority)" class="mr-1" v-if="currentTab === 'closed'">mdi-flag</v-icon>
          <span class="priority-dot" :style="{ background: getPriorityColor(item.priority) }" v-else></span>
          <span>{{ getPriorityLabel(item.priority) }}</span>
        </div>
      </template>

      <template #col-status="{ item }">
        <div class="status-pill-wrap">
          <div
            class="status-pill"
            :style="getStatusStyles(item.status)"
            @click.stop="toggleStatusMenu($event, item)"
          >
            <span class="status-dot" :style="{ background: getStatusColor(item.status) }"></span>
            <span class="status-label">{{ getStatusLabel(item.status) }}</span>
            <v-icon size="12" :color="getStatusColor(item.status)" class="ml-1">mdi-chevron-down</v-icon>
          </div>
        </div>
      </template>

      <template #col-created_on="{ item }">
        <span class="date-text">{{ item.created_on | formatDate }}</span>
      </template>
    </PremiumTable>

    <!-- Confirm Dialog -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Ticket"
      message="Are you sure you want to delete this help desk ticket?"
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteComplaints"
    />

    <!-- ── Status Floating Dropdown ── -->
    <div
      v-if="statusMenu.open"
      class="status-dropdown-portal"
      :style="{ top: statusMenu.y + 'px', left: statusMenu.x + 'px' }"
      @click.stop
    >
      <div
        v-for="s in statusList"
        :key="s.value"
        class="status-option"
        :class="{ active: statusMenu.item && statusMenu.item.status === s.value }"
        @click="selectStatus(statusMenu.item, s.value)"
      >
        <span class="status-dot" :style="{ background: s.color }"></span>
        <span>{{ s.name }}</span>
        <v-icon v-if="statusMenu.item && statusMenu.item.status === s.value" size="12" color="var(--brand)" class="ml-auto">mdi-check</v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "ComplaintsList",
  mixins: [filterMixin],
  components: { PremiumTable, ConfirmModal, FilterDropdown },

  data() {
    return {
      search: "",
      complaints: [],
      totalRecords: 0,
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      currentTab: "open",
      tabs: [
        { id: "open", name: "Active Tickets", icon: "mdi-alert-circle-outline" },
        { id: "closed", name: "Closed Tickets", icon: "mdi-check-circle-outline" },
      ],
      current_status: null,
      statusList: [
        { name: "Pending", value: 0, color: "#F59E0B" },
        { name: "In Progress", value: 1, color: "#10B981" },
        { name: "Completed", value: 2, color: "#3B82F6" },
        { name: "Cancelled", value: 3, color: "#EF4444" },
      ],
      user: {},
      statusMenu: { open: false, x: 0, y: 0, item: null },
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor"]),

    columns() {
      return [
        {
          key: "title",
          label: this.$t("complaints.headers.title") || "Issue Details",
          minWidth: "200px"
        },
        {
          key: "added_by",
          label: this.$t("complaints.headers.added_by") || "Requester",
          width: "220px",
        },
        {
          key: "house_number",
          label: this.$t("complaints.headers.house_no") || "Unit",
          width: "120px",
        },
        {
          key: "priority",
          label: this.$t("complaints.headers.priority") || "Priority",
          width: "120px",
        },
        {
          key: "status",
          label: this.$t("complaints.headers.status") || "Status",
          width: "160px",
        },
        {
          key: "created_on",
          label: this.$t("complaints.headers.created_at") || "Date",
          width: "140px",
        },
      ];
    },

    filter_status() {
      if (this.currentTab === "open") {
        return this.statusList.filter((s) => [0, 1].includes(s.value));
      } else {
        return this.statusList.filter((s) => [2, 3].includes(s.value));
      }
    },
  },

  watch: {
    page() {
      this.getComplaints();
    },
    itemsPerPage() {
      this.getComplaints();
    },
    search() {
      this.page = 1;
      this.getComplaints();
    },
    activeTabId(val) {
      this.currentTab = val || "open";
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("society", ["setSelectedTab"]),

    onSearch(val) {
      this.search = val;
    },

    debounceSearch: debounce(function () {
      this.getComplaints();
    }, 500),

    onOptionsUpdate(opt) {
      this.options.sortBy = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getComplaints();
    },

    switchTab(tabId) {
      this.currentTab = tabId;
      this.activeTabId = tabId;
      this.current_status = null; // Reset filter when switching tabs
      this.setSelectedTab({ selectedTab: tabId });
      this.page = 1;
      this.getComplaints();
    },

    onFilterChange(val) {
      this.current_status = val;
      this.page = 1;
      this.getComplaints();
    },

    getComplaints() {
      this.loading = true;
      const sortBy = this.options.sortBy[0] || "";
      const sortDesc = this.options.sortDesc[0] || "";

      const filterVal = this.current_status === null || this.current_status === "" ? -1 : this.current_status;

      const params = [
        `filter=${filterVal}`,
        `page=${this.page}`,
        `perPage=${this.itemsPerPage}`,
        `sortBy=${sortBy}`,
        `sortDesc=${sortDesc}`,
        `search=${this.search || ""}`,
      ].join("&");

      const endpoint = this.currentTab === "open" ? "open" : "closed";

      this.$axios
        .get(`societyadmin/complaints/${endpoint}?${params}`)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.complaints = data.complaints;
            this.totalRecords = data.total;
            if (data.page > data.lastPage) this.page = 1;
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    updateStatus(id, value) {
      this.$axios
        .put(`societyadmin/complaints/updateStatus/${id}/${value}`)
        .then((response) => {
          const data = response.data;
          if (data.status) {
            this.getComplaints();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        });
    },

    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteComplaints() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/complaints/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getComplaints();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    getPriorityLabel(p) {
      const labels = { 0: "Emergency", 1: "Low", 2: "Medium", 3: "High" };
      return labels[p] || "General";
    },
    getPriorityColor(p) {
      const colors = { 0: "#EF4444", 1: "#94A3B8", 2: "#3B82F6", 3: "#F59E0B" };
      return colors[p] || "#64748B";
    },
    getPriorityStyles(p) {
      const color = this.getPriorityColor(p);
      if (this.currentTab === "closed") {
        return {
          background: color + "12",
          color: color,
          border: `1px solid ${color}20`,
        };
      } else {
        return {
          background: color + "12",
          color: color,
        };
      }
    },
    getStatusClass(s) {
      const classes = {
        0: "s-pending",
        1: "s-progress",
        2: "s-closed",
        3: "s-cancelled",
      };
      return classes[s] || "";
    },
    getStatusLabel(s) {
      const labels = { 0: "Pending", 1: "In Progress", 2: "Completed", 3: "Cancelled" };
      return labels[s] ?? "Pending";
    },
    getStatusColor(s) {
      const colors = { 0: "#F59E0B", 1: "#10B981", 2: "#3B82F6", 3: "#EF4444" };
      return colors[s] ?? "#F59E0B";
    },
    getStatusStyles(s) {
      const color = this.getStatusColor(s);
      return {
        background: color + "15",
        color: color,
        border: `1px solid ${color}30`,
      };
    },
    toggleStatusMenu(event, item) {
      if (this.statusMenu.open && this.statusMenu.item === item) {
        this.statusMenu.open = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.statusMenu = {
        open: true,
        x: rect.left,
        y: rect.bottom + 4,
        item,
      };
    },
    selectStatus(item, value) {
      this.statusMenu.open = false;
      if (!item || item.status === value) return;
      item.status = value;
      this.updateStatus(item.id, value);
    },
    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY") : "N/A";
    },
    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Close status dropdowns when clicking outside
    document.addEventListener('click', () => {
      this.statusMenu.open = false;
    });

    // Initialize persistent filters
    this.initFilters('complaints');

    if (this.activeTabId) {
      this.currentTab = this.activeTabId;
    } else if (this.$store.state.society.selectedTab) {
      const savedTab = this.$store.state.society.selectedTab;
      if (savedTab === "Active Tickets" || savedTab === "open") this.currentTab = "open";
      if (savedTab === "Closed Tickets" || savedTab === "closed") this.currentTab = "closed";
    }

    this.getComplaints();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Header Controls ── */
.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.status-filter-wrap {
  display: flex;
}

/* ── Custom Tab Pills ── */
.tab-pill-wrap {
  display: flex;
  gap: 4px;
  background: var(--surface2, #f8fafc);
  padding: 4px;
  border-radius: 12px;
  border: 1px solid var(--border, #e2e8f0);
}
.tab-pill-btn {
  display: flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx3, #6b7280);
  transition: all 0.2s;
  border: none;
  background: transparent;
  cursor: pointer;
}
.tab-pill-btn:hover {
  color: var(--tx2, #374151);
}
.tab-pill-btn.active {
  background: #ffffff;
  color: var(--brand, #0d9972);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* ── Cells ── */
.added-by-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  /* font-weight: 700; */
  color: var(--tx, #111827);
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}

.title-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}
.ticket-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
}
.ticket-desc {
  font-size: 11px;
  color: var(--tx3);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
}

/* Priority Pill */
.priority-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.priority-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── Status Pill Dropdown ── */
.status-pill-wrap {
  position: relative;
  display: inline-block;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
  white-space: nowrap;
  border: 1px solid transparent;
}
.status-pill:hover {
  filter: brightness(0.95);
  transform: translateY(-1px);
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-label {
  flex: 1;
}

/* Dropdown panel */
.status-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 999;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  min-width: 150px;
  overflow: hidden;
  animation: dropdownFadeIn 0.15s ease;
}

@keyframes dropdownFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.status-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2);
  cursor: pointer;
  transition: background 0.15s;
}
.status-option:hover {
  background: var(--surface2);
}
.status-option.active {
  background: var(--surface2);
  color: var(--tx);
}

/* Inline Status Select Override */
.inline-status-select {
  max-width: 150px;
  font-size: 12.5px;
  font-weight: 600;
}

::v-deep .inline-status-select .v-input__slot {
  height: 32px !important;
  padding: 0 10px !important;
  border-radius: 8px !important;
  border: 1px solid var(--border) !important;
  background: var(--surface2) !important;
  box-shadow: none !important;
}
::v-deep .inline-status-select fieldset {
  border: none !important;
}
::v-deep .inline-status-select .v-select__selection {
  margin: 0;
  font-size: 13px;
}
::v-deep .inline-status-select .v-input__append-inner {
  margin-top: 6px;
}

.s-pending ::v-deep .v-input__slot {
  border-color: rgba(245, 158, 11, 0.3) !important;
  background: rgba(245, 158, 11, 0.05) !important;
}
.s-progress ::v-deep .v-input__slot {
  border-color: rgba(16, 185, 129, 0.3) !important;
  background: rgba(16, 185, 129, 0.05) !important;
}
.s-closed ::v-deep .v-input__slot {
  border-color: rgba(59, 130, 246, 0.3) !important;
  background: rgba(59, 130, 246, 0.05) !important;
}
.s-cancelled ::v-deep .v-input__slot {
  border-color: rgba(239, 68, 68, 0.3) !important;
  background: rgba(239, 68, 68, 0.05) !important;
}

.status-name {
  font-weight: 700;
}

.date-text {
  font-size: 12.5px;
  color: var(--tx3);
  font-weight: 500;
  white-space: nowrap;
}

/* ── Tablet Responsive ── */
@media (max-width: 960px) {
  .header-controls {
    width: 100%;
    flex-wrap: nowrap;
  }
  .tab-pill-wrap {
    flex: 1;
    min-width: 0;
  }
  .tab-pill-btn {
    flex: 1;
    justify-content: center;
  }
  .status-filter-wrap {
    flex: 1;
    min-width: 0;
  }
  .status-filter-wrap .fdd-wrap {
    width: 100%;
  }
  .status-filter-wrap ::v-deep .fdd-trigger {
    width: 100% !important;
    max-width: none !important;
  }
}

/* ── Mobile Responsive ── */
@media (max-width: 600px) {
  .page-wrap {
    padding: 12px;
  }
  .header-controls {
    width: 100%;
    gap: 8px;
    flex-direction: column;
    align-items: stretch;
  }
  .tab-pill-wrap {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    flex: none;
  }
  .tab-pill-wrap::-webkit-scrollbar {
    display: none;
  }
  .tab-pill-btn {
    padding: 5px 10px;
    font-size: 11px;
    white-space: nowrap;
    flex: 1;
    justify-content: center;
  }
  .status-filter-wrap {
    width: 100%;
    flex-shrink: 0;
  }
  .status-filter-wrap .fdd-wrap {
    width: 100%;
  }
  .status-filter-wrap .fdd-trigger {
    width: 100%;
    max-width: 100%;
    justify-content: space-between;
  }
}
</style>

<style>
/* Fixed-position status dropdown portal — outside scoped so it renders above everything */
.status-dropdown-portal {
  position: fixed;
  z-index: 9999;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
  min-width: 155px;
  overflow: hidden;
  animation: sdpFadeIn 0.15s ease;
}

@keyframes sdpFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.status-dropdown-portal .status-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: background 0.12s;
}
.status-dropdown-portal .status-option:hover {
  background: var(--surface2, #f8fafc);
}
.status-dropdown-portal .status-option.active {
  background: var(--surface2, #f8fafc);
  color: var(--tx, #111827);
}
.status-dropdown-portal .status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
